import { parseFontString } from "../../../shared/utils/common";
import { AudioLayerData } from "../layers/audioLayerData.model";
import { ColorLayerData } from "../layers/colorLayerData.model"
import { Effect } from "../layers/effect.model";
import { EffectStep } from "../layers/effectStep.model";
import { DropShadowEffect, OpacityEffect, PositionEffect, ScaleEffect } from "../layers/effectsType.model";
import { ImageLayerData } from "../layers/imageLayerData.model";
import { LayerData } from "../layers/layerData.model";
import { TextLayerData } from "../layers/textLayerData.model";
import { VideoLayerData } from "../layers/videoLayerData.model";
import { LayerAttributes, LayerDataType } from "../types/layerType";


// Layers
export function parseLayerDataJSON(data: any) {
    switch (data.type) {
        case ("color"):
            return new ColorLayerData(data);
        case ("image"):
            return new ImageLayerData(data);
        case ("video"):
            return new VideoLayerData(data);
        case ("text"):
            return new TextLayerData(data);
        case ("audio"):
            return new AudioLayerData(data);
        default:
            console.warn(`Instantiated an effect with unknown type ${data.type}`)
            return new LayerData(data);
    }
}

export function getFontAttributes(textLayerData: TextLayerData) {
    const { familyName, weight, italic } = parseFontString(textLayerData.font);

    let fontStyle;
    if (italic && weight != '700') {
        fontStyle = "italic"
    } else if (weight == '700' && italic) {
        fontStyle = "italic bold"
    } else if (weight == '700') {
        fontStyle = 'bold';
    } else {
        fontStyle = 'normal'
    }

    const fontAttributes: { [key: string]: any } = {
        fontSize: textLayerData.font_size,
        verticalAlign: "middle",
        align: textLayerData.horizontal_align,
        fontFamily: familyName,
        fontStyle: fontStyle,
        wrap: "word"
    };

    if (textLayerData.font_size === 0) {
        delete fontAttributes.fontSize;
    }
    return fontAttributes
}

export function getStepContext(effect: Effect, time: number): [EffectStep | null, EffectStep | null, EffectStep | null] {
    let prev: EffectStep | null = null
    let curr: EffectStep | null = null
    let next: EffectStep | null = null

    for (let i = 0; i < effect.steps.length; i++) {
        const step = effect.steps[i]
        if (step.time === time) {
            curr = step
            prev = effect.steps[i - 1] ?? null
            next = effect.steps[i + 1] ?? null
            break
        }
        if (step.time > time) {
            next = step
            break
        }
        prev = step
    }
    return [prev, curr, next]
}

function linear(t: number): number {
    return t;
}

function easeOut(t: number): number {
    return t * t;
}

function easeIn(t: number): number {
    return t * (2 - t);
}

function easeInOut(t: number): number {
    return t < 0.5
        ? 2 * t * t
        : -1 + (4 - 2 * t) * t;
}

function getProgressFunction(prevMode: string, nextMode: string): (t: number) => number {
    let prevEase = false;
    let nextEase = false;
    if (["ease_out", "ease_inout"].includes(prevMode)) prevEase = true;
    if (["ease_in", "ease_inout"].includes(nextMode)) nextEase = true;

    if (prevEase && nextEase) return easeInOut
    if (prevEase) return easeOut
    if (nextEase) return easeIn
    return linear;
}

export function getValueAtTime(effect: Effect, time: number) {
    if (effect.steps.length === 0) {
        throw new Error("No steps to interpolate");
    }

    const sorted = [...effect.steps].sort((a, b) => a.time - b.time);

    if (time <= sorted[0].time) return sorted[0].value;
    if (time >= sorted[sorted.length - 1].time) return sorted[sorted.length - 1].value;

    let prev = sorted[0];
    let next = sorted[sorted.length - 1];

    for (let i = 0; i < sorted.length - 1; i++) {
        const a = sorted[i];
        const b = sorted[i + 1];
        if (time >= a.time && time <= b.time) {
            prev = a;
            next = b;
            break;
        }
    }

    const t0 = prev.time;
    const t1 = next.time;
    const rawProgress = (time - t0) / (t1 - t0);

    const prevMode = prev.mode || "linear";
    const nextMode = next.mode || "linear";

    const easingFn = getProgressFunction(prevMode, nextMode);
    const progress = easingFn(rawProgress);

    const v0 = prev.value;
    const v1 = next.value;

    if (typeof v0 === "number" && typeof v1 === "number") {
        return v0 + (v1 - v0) * progress;
    }

    if (Array.isArray(v0) && Array.isArray(v1) && v0.length === v1.length) {
        return v0.map((start, i) => start + (v1[i] - start) * progress);
    }

    throw new Error("Incompatible value types for interpolation");
}




interface Override {
    object_type: string;
    object_id: number;
    field_name: string;
    override_value: any;
}

export function getLayerAsOverrides(layer: LayerData) {
    const overrides: Override[] = [];
    const layerToObjectType = new Map<string, string>([
        ['text', 'text_layer'],
        ['color', 'color_layer'],
        ['audio', 'audio_layer'],
        ['image', 'image_layer'],
        ['video', 'video_layer'],
        ['composition', 'layer'],
    ]);

    const effectToObjectType = new Map<string, string>([
        ['opacity', 'opacity_effect'],
        ['scale', 'scale_effect'],
        ['position', 'position_effect'],
        ['dropshadow', 'dropshadow_effect']
    ])

    const extractFields = (
        obj: any,
        type: string,
        keys: string[] = [],
        mode: "include" | "exclude",
    ) => {
        for (const key of Object.keys(obj)) {
            if (mode === "include" && !keys.includes(key)) continue;
            if (mode === "exclude" && keys.includes(key)) continue;
            if (obj[key] === undefined) continue;

            const idToOverride = Number(obj.id)
            if (Number.isNaN(idToOverride)) continue;

            if (idToOverride === undefined) {
                console.error("Cannot find the proper id to override, skipping...");
                continue;
            }
            if (type === "color_layer" && key === "color") {
                overrides.push({
                    object_type: type, // Sorry...
                    object_id: idToOverride,
                    field_name: "color_value",
                    override_value: obj[key],
                })
            } else {
                overrides.push({
                    object_type: type,
                    object_id: idToOverride,
                    field_name: key,
                    override_value: obj[key],
                });
            }
        }
    };

    const EXCLUDED_LAYER_KEYS = ["children", "effects", "path", "parent_id", "type", "name", "id"]
    const INCLUDED_EFFECT_KEYS = [] as string[]
    const INCLUDED_STEP_KEYS = ["time", "value", "mode"]

    if (!layerToObjectType.get(layer.type)) {
        console.error("Unknown layer type passed for override");
        return;
    }


    extractFields(layer, "layer", EXCLUDED_LAYER_KEYS, "exclude");
    
    for (const effect of layer.effects || []) {
        if (!effectToObjectType.get(effect.type)) {
            console.error("Unknown effect type passed for override");
            return;
        }

        extractFields(effect, "effect", INCLUDED_EFFECT_KEYS, "include");
        if (Object.keys(effect.params).length > 0) {
            extractFields({...effect.params, "id": effect.id}, "effect", ["id"], "exclude")
        }
        for (const step of effect.steps || []) {
            extractFields(step, "step", INCLUDED_STEP_KEYS, "include");
        }
    }
    return overrides
}

export function getEffectAttributeAtTime(effect: Effect, time: number) {
    const value = effect.steps.length > 0 ? getValueAtTime(effect, time) : "";

    switch (effect.type) {
        case "opacity":
            return { opacity: value / 100 };

        case "scale":
            return { scaleX: value[0] / 100, scaleY: value[1] / 100 }

        case "position":
            return { x: value[0], y: value[1] }

        case "dropshadow":
            return { shadowBlur: effect.params.radius, shadowOffset: { x: effect.params.offset[0], y: effect.params.offset[1] }, shadowOpacity: effect.params.opacity }

        default:
            return {}
    }
}

export function getLayerAttributesAtTime(layer: LayerDataType, time: number) {
    let layerAttributes = {
        visible: true,
        opacity: 1,
        x: 0,
        y: 0,
        scaleX: 1,
        scaleY: 1,
    } as LayerAttributes

    if (time < layer.start_time || time > layer.end_time || layer.visible === false) {
        layerAttributes.visible = false;
    }

    layer.effects.forEach((effect: Effect) => {
        layerAttributes = { ...layerAttributes, ...getEffectAttributeAtTime(effect, time - layer.start_time) };
    })

    if (layerAttributes.shadowOffset) {
        layerAttributes.shadowOffset = {x: layerAttributes.shadowOffset.x * layerAttributes.scaleX,  y: layerAttributes.shadowOffset.y * layerAttributes.scaleY}
    }

    return layerAttributes
}


// Effects
export function parseEffectDataJSON(data: any) {
    switch (data.type) {
        case ("position"):
            return new PositionEffect(data);

        case ("opacity"):
            return new OpacityEffect(data);

        case ("scale"):
            return new ScaleEffect(data);

        case ("dropshadow"):
            return new DropShadowEffect(data);

        default:
            console.warn(`Instantiated an effect with unknown type ${data.type}`)
            return new Effect(data);
    }
}

// Steps

